import { PlainCard } from "../../../components/settings_components";

import React, { useCallback, useState } from "react";
import { type EnvVar } from "../../../utils/types";
import { getCompanySettings } from "../../../utils/firebase_operations";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { timestampToDate, trackSentryException } from "../../../utils/helpers";
import { arrayRemove, doc, getDoc, updateDoc } from "@firebase/firestore";
import { customFirestore } from "../../../firebase";
import { auditMeta } from "../../../utils/auditMeta";
import { useAtomValue, useSetAtom } from "jotai";
import { getTestsAtom } from "../../../atoms/testing-settings-atoms";
import { companyDataAtom } from "../../test-run/test-run-atoms";
import {
  DataGrid,
  GridActionsCellItem,
  type GridColDef,
  type GridRenderCellParams,
  gridClasses,
} from "@mui/x-data-grid";
import { Delete, Edit } from "@mui/icons-material";
import EditEnvVarDialog, {
  editEnvVarDialogStateAtom,
} from "./edit-env-var-dialog";
import { Button } from "@mui/material";
import AddEnvVarDialog, {
  addEnvVarDialogStateAtom,
} from "./add-env-var-dialog";
import { AsyncConfirmationModal } from "../../../components/asyncConfirmationModal";
import { customAlertStateAtom } from "../../../components/custom-alert";

const EnvVarsPanel = ({ companyId }: { companyId: string | undefined }) => {
  const testListQuery = useAtomValue(
    getTestsAtom({ organizationId: companyId! })
  );

  const queryClient = useQueryClient();

  const envVarsQuery = useQuery({
    queryKey: ["getEnvVars", companyId],
    queryFn: async () => {
      const firestoreSettings = await getCompanySettings(companyId);

      if (firestoreSettings) {
        const envVariables = (firestoreSettings.envVars as EnvVar[]) ?? [];
        console.log({ envVariables });
        return envVariables;
      } else {
        return [];
      }
    },
    refetchOnWindowFocus: false,
  });

  const [deleteEnvVarDialogState, setDeleteEnvVarDialogState] = useState<
    string | null
  >(null);

  const resetDeleteEnvVarDialogState = useCallback(() => {
    setDeleteEnvVarDialogState(null);
  }, []);

  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const deleteEnvVarMutation = useMutation({
    mutationKey: ["deleteEnvVar"],
    mutationFn: async () => {
      try {
        console.log(
          `Deleting env var ${deleteEnvVarDialogState!} for company ${companyId!}`
        );
        const settingsDoc = doc(customFirestore, "settings", companyId!);
        const envVars: EnvVar[] =
          (await getDoc(settingsDoc)).data()?.envVars ?? [];
        const foundEnvVar = envVars.find(
          (envVar) => envVar.key === deleteEnvVarDialogState
        );

        if (foundEnvVar) {
          await updateDoc(settingsDoc, {
            envVars: arrayRemove(foundEnvVar),
            ...auditMeta(),
          });
          await queryClient.invalidateQueries({
            queryKey: ["getEnvVars", companyId],
          });
          companyDataAtom.remove(companyId);
          queryClient.removeQueries({
            queryKey: ["getCompanyData"],
          });
          setCustomAlertState({
            open: true,
            type: "success",
            title: `Env var "${deleteEnvVarDialogState!}" deleted successfully`,
          });
        } else {
          setCustomAlertState({
            open: true,
            type: "error",
            title: "Something went wrong",
            description: `Env Var with key ${deleteEnvVarDialogState!} not found`,
          });
        }
      } catch (error: any) {
        trackSentryException(error);
        console.error("An error occurred during deleting", error);
        setCustomAlertState({
          open: true,
          type: "error",
          title: "Something went wrong",
          description: "An error occurred during deleting",
        });
      }
      resetDeleteEnvVarDialogState();
    },
  });

  const setEditEnvVarDialogState = useSetAtom(editEnvVarDialogStateAtom);

  const columns: GridColDef[] = [
    { field: "key", headerName: "Key", flex: 1 },
    {
      field: "value",
      headerName: "Value",
      flex: 1,
      renderCell: (params) => {
        const row = params.row;
        if (!row.isSecret) {
          return <p>{row.value}</p>;
        } else {
          return <p>••••••</p>;
        }
      },
    },
    {
      field: "lastEditTimestamp",
      minWidth: 256,
      headerName: "Last Edited At",
      renderCell: (params: GridRenderCellParams) => {
        const lastEditTimestamp = params.value as number;
        if (lastEditTimestamp) {
          return <p>{timestampToDate(lastEditTimestamp)}</p>;
        } else {
          return <p>N/A</p>;
        }
      },
    },
    {
      field: "actions",
      type: "actions",
      minWidth: 128,
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ row }) => {
        return [
          <GridActionsCellItem
            key={"edit"}
            icon={<Edit />}
            label="Edit"
            className="textPrimary"
            onClick={() => {
              setEditEnvVarDialogState({
                open: true,
                envVarKey: row.key,
              });
            }}
            color="inherit"
          />,
          <GridActionsCellItem
            key={"delete"}
            icon={<Delete />}
            label="Delete"
            onClick={() => {
              setDeleteEnvVarDialogState(row.key);
            }}
            color="inherit"
          />,
        ];
      },
    },
  ];

  const setAddEnvVarDialogState = useSetAtom(addEnvVarDialogStateAtom);

  const handleOpenAddEnvVarDialog = useCallback(() => {
    setAddEnvVarDialogState({ open: true, envVars: envVarsQuery.data! });
  }, [envVarsQuery.data, setAddEnvVarDialogState]);

  return (
    <PlainCard>
      <AddEnvVarDialog />
      <EditEnvVarDialog />
      <AsyncConfirmationModal
        title={"Are you sure you want to delete the env var?\n"}
        actionTitle={"Delete"}
        backTitle={"Cancel"}
        closeHandler={resetDeleteEnvVarDialogState}
        description={`Env var key: ${deleteEnvVarDialogState ?? ""}`}
        open={deleteEnvVarDialogState != null}
        actionHandler={deleteEnvVarMutation.mutateAsync}
        actionIcon={<Delete />}
        submitting={deleteEnvVarMutation.isPending}
      />
      <div className="grid grid-cols-2">
        <h1 className="text-3xl pl-4 pb-2 font-bold">Env Variables</h1>
        <div className="place-self-end mt-2 pr-2">
          <Button
            variant="outlined"
            onClick={handleOpenAddEnvVarDialog}
            size={"large"}
          >
            + Add Env Variable
          </Button>
        </div>
      </div>
      <div className="pl-4 pr-4 pt-4 relative sm:rounded-lg overflow-auto h-[calc(100%-50.25px)]">
        <DataGrid
          showCellVerticalBorder={false}
          showColumnVerticalBorder={false}
          disableColumnMenu={true}
          className={"bg-white"}
          rows={envVarsQuery.data}
          loading={envVarsQuery.isLoading || testListQuery.isLoading}
          columns={columns}
          getRowId={(row) => row.key}
          isRowSelectable={(row) => false}
          initialState={{
            sorting: {
              sortModel: [{ field: "key", sort: "asc" }],
            },
          }}
          sx={{
            [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
              outline: "transparent",
            },
            [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
              {
                outline: "none",
              },
          }}
        />
      </div>
    </PlainCard>
  );
};

export { EnvVarsPanel };
